import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import useStyleSheet from "./useStyleSheet";
import MarketingTopNav from "./components/MarketingTopNav";
import BrandMark from "./components/BrandMark";
import BuzzPromoSection from "./components/BuzzPromoSection";
import OrderingPromoSection from "./components/OrderingPromoSection";

// Μετάβαση v5 (5/11/2026): οι ενότητες Ordering/Buzz με δημόσια εγγραφή μένουν κρυφές μέχρι να
// είναι έτοιμα Turnstile (anti-bot), SMTP και τελικές τιμές. Για δημόσια έναρξη: true + νέο build.
const SHOW_PRODUCT_PROMOS = false;

/**
 * estiasync Marketing Landing Page – v3 (i18n + geo‑default)
 * -----------------------------------------------------------------
 * • Two languages (English + Greek)
 * • Auto‑select Greek when visiting from Greece (via IP geo with graceful fallbacks)
 * • Manual language switcher (flags in TopNav) with localStorage persistence
 * • Promo video switches between /promo_en.mp4 and /promo_gr.mp4
 *
 * Quick setup:
 *   - Place promo files at /public/promo_en.mp4 and /public/promo_gr.mp4
 *   - Keep this file as your MarketingHome root component
 */

// -----------------------------
// i18n helpers
// -----------------------------
const tr = {
  en: {
    nav: {
      features: "Features",
      how: "How it works",
      pricing: "Pricing",
      faq: "FAQ",
	  contact: "Contact",
      signin: "Sign in",
    },
    badge: "Built for restaurants • Service Excellence",
	hero: {
	  h1a: "Service in",
	  h1b: "perfect sync",
	  p: "EstiaSync is the all-in-one tool for restaurants. It’s not another order-taking system — it’s a complete platform that connects reservations, guest profiles, BBQ, kitchen and service in real time.",
	  explore: "See the features",
	  watch: "Watch the short video",
	  p1dt: "Push Notifications",
	  p1dd: "Instant, automated alerts",
	  p2dt: "Easy-to-use screens",
	  p2dd: "The right info at every station",
	},
    social: { trusted: "Trusted by teams that move fast" },
	features: {
	  h2: "Everything in one platform",
	  p: "So your whole operation runs like clockwork.",
	  items: [
		{ t: "Guestbook & CRM", d: "Fast search and add, caller ID lookup, full guest history, ratings and notes." },
		{ t: "Floor plan & tables", d: "Map tables on your layout, split areas/stations, filter smartly, set capacities and merge tables." },
		{ t: "Create a reservation", d: "Search by phone/name or caller ID with instant history; see availability via timeline & table agenda." },
		{ t: "Manage reservations", d: "Lists/cards with one-tap calling, editing guest & booking details, filters, statuses and arrivals routing." },
		{ t: "BBQ station", d: "Grouped grill orders per reservation, quantities and timings, prep planning, timers and label printing." },
		{ t: "Kitchen station", d: "Actions per reservation (welcome, starters, sides, dessert) with timers and start/finish notifications." },
		{ t: "Service station", d: "Live floor plan with status colors, flashing pending actions, table card with everything in one place." },
		{ t: "Weekly schedule", d: "Shifts per station/area, auto-repeat weekly, staff see the plan live anytime, anywhere." },
		{ t: "Personal alerts & smartwatches", d: "Targeted service notifications per area, beyond on-screen audio/visual cues." },
		{ t: "Analytics & reports", d: "Visits, consumption, average execution times, and preference heatmaps — built for daily improvement." },
		{ t: "Service configuration", d: "Items, BBQ categories, preparation times and the settings that shape your workflow." },
		{ t: "Users & roles", d: "User management with fine-grained access per station and responsibilities." },
	  ],
	},
	how: {
	  h2: "Ready to fire it up?",
	  p: "Here’s how onboarding works, step by step.",
	  plans: [
		{
		  name: "Starter",
		  steps: [
			"Contact us by email (info@estiasync.com) or call us at +30 2294 701845.",
			"A specialist will guide you based on your restaurant’s needs.",
			"Your account is ready — we meet on-site for the next step.",
			"We tailor the setup to your exact flow.",
			"If equipment is needed, we propose options as part of a complete solution.",
			"Training time — we onboard and train your staff.",
			"Done: reservations and guestbook available anywhere, anytime.",
		  ],
		},
		{
		  name: "Basic",
		  steps: [
			"Ready for the next steps — let’s do it together.",
			"We install the required equipment for BBQ/kitchen and service so everyone stays in sync.",
			"We train staff per station, based on role.",
			"Now reservations, BBQ/kitchen and service move as one team.",
		  ],
		},
		{
		  name: "Pro",
		  steps: [
			"Think that was all? We make your operation run like clockwork.",
			"Service actions live, in real time, on phones and smartwatches.",
			"With weekly scheduling per station, targeted alerts guide the service team instantly.",
			"Tick-tock — that’s EstiaSync keeping your shift in sync.",
		  ],
		},
	  ],
	},
    screens: {
      h2: "Screens made for real shifts",
      p: "Big touch targets. Clear status. Swap between stations quickly.",
      shots: [
        { name: "Reservations", hint: "List + quick add" },
        { name: "BBQ", hint: "Prep board" },
        { name: "Service", hint: "Runner mode" },
        { name: "Kitchen", hint: "Ticket view" },
      ],
    },
    pricing: {
      h2: "Simple pricing",
      monthly: "Monthly",
      yearly: "Yearly",
      yearlyNote: "Get 2 months free/year!",
      plans: {
        starter: {
          name: "Starter",
          features: ["Single location", "Areas & Tables", "Clients Management", "Reservations Management"],
          cta: "Try the demo",
        },
        basic: {
          name: "Basic",
          features: ["Everything in Starter", "BBQ board", "Kitchen view", "Service runner"],
          cta: "Try the demo",
        },
        pro: {
          name: "Pro",
          badge: "Popular",
          features: ["Everything in Basic", "Analytics & Reporting", "Employees Scheduler", "Automatic Push Notifications"],
          cta: "Try the demo",
        },
        multi: {
          name: "Multi-site",
          price: "Talk to us",
          features: ["Everything in Pro", "Multiple locations", "Priority support", "Custom onboarding"],
          cta: "Contact",
        },
      },
    },
    faq: {
      h2: "FAQ",
      p: (emailNode) => <>Anything else? Email {emailNode}.</>,
      items: [
        { q: "Does it work on iPhone/iPad?", a: "Yes. It’s a PWA — install it to Home Screen and it runs fullscreen." },
        { q: "Do I need special hardware?", a: "No. Any modern browser works — phones, tablets, laptops, side screens." },
        { q: "Can BBQ and kitchen see different views?", a: "Yes, each station has its own live board from the same data." },
        { q: "How long to onboard?", a: "Most teams run a test shift the same day. Invite staff and go." },
        { q: "What about notifications?", a: "Staff can opt‑in to push notifications for key events." },
      ],
    },
	final: {
	  h3: "Request an automated demo",
	  p: "Submit your details and, after approval, we’ll email you the URL and access credentials.",
	  demo: "Request demo access",
	  call: "Contact us",
	},
    footer: { privacy: "Privacy", terms: "Terms", copyright: () => `© ${new Date().getFullYear()} estiasync.com` },
	legal: {
	  privacy: {
		title: "Privacy Policy",
		updated: "Last updated: 24 December 2025",
		intro:
		  "This Privacy Policy explains how EstiaSync (“we”, “us”) collects and uses information when you visit our website or use our services.",
		sections: [
		  {
			h: "What data we collect",
			bullets: [
			  "Contact details you provide (e.g., name, email, phone).",
			  "Account and operational data you enter in the service (e.g., reservations, customer notes, floor/table setup).",
			  "Technical data (e.g., device/browser, approximate location, IP address, logs) for security and troubleshooting.",
			  "Cookies / similar technologies for basic functionality and performance.",
			],
		  },
		  {
			h: "How we use your data",
			bullets: [
			  "To provide and operate the service (accounts, features, support).",
			  "To communicate with you (requests, onboarding, support).",
			  "To improve reliability, performance, and user experience.",
			  "To maintain security, prevent abuse, and comply with legal obligations.",
			],
		  },
		  {
			h: "Legal basis (GDPR)",
			bullets: [
			  "Performance of a contract (to deliver the service).",
			  "Legitimate interests (security, service improvement).",
			  "Consent (where required, e.g., certain cookies/marketing).",
			  "Legal obligations (where applicable).",
			],
		  },
		  {
			h: "Sharing with third parties",
			bullets: [
			  "We may use trusted providers (e.g., hosting, email delivery) to run the service.",
			  "We do not sell your personal data.",
			  "We may disclose data if required by law or to protect our rights and security.",
			],
		  },
		  {
			h: "Data retention",
			bullets: [
			  "We keep data only for as long as needed for the purposes described above, unless a longer period is required by law.",
			  "You may request deletion of your account data, subject to legal/operational constraints.",
			],
		  },
		  {
			h: "Security",
			bullets: [
			  "We apply technical and organizational measures to protect data.",
			  "No system is 100% secure; please use strong passwords and keep devices protected.",
			],
		  },
		  {
			h: "Your rights",
			bullets: [
			  "Access, rectification, deletion, restriction, portability, and objection (where applicable).",
			  "You can also withdraw consent at any time (when processing is based on consent).",
			],
		  },
		  {
			h: "Contact",
			bullets: [
			  "Email: info@estiasync.com",
			  "Phone: +30 2294 701 845",
			],
		  }
		],
	  },

	  terms: {
		title: "Terms of Use",
		updated: "Last updated: 24 December 2025",
		intro:
		  "These Terms govern your access to and use of the EstiaSync website and services. By using EstiaSync, you agree to these Terms.",
		sections: [
		  {
			h: "Service scope",
			bullets: [
			  "EstiaSync provides tools for restaurant operations (e.g., reservations, floor plan, station views, notifications).",
			  "Some features may depend on device/browser capabilities (e.g., PWA/push notifications).",
			],
		  },
		  {
			h: "Accounts & responsibilities",
			bullets: [
			  "You are responsible for maintaining the confidentiality of your credentials.",
			  "You must ensure that data you enter is lawful and that you have the right to use it.",
			],
		  },
		  {
			h: "Acceptable use",
			bullets: [
			  "Do not misuse the service (e.g., hacking, scraping, or disrupting operations).",
			  "Do not upload unlawful content or content that violates third-party rights.",
			],
		  },
		  {
			h: "Trials, pricing & payments",
			bullets: [
			  "If you use a demo/trial, access may be limited and can be withdrawn.",
			  "Paid plans, billing terms, and any invoices are agreed separately (if applicable).",
			],
		  },
		  {
			h: "Availability",
			bullets: [
			  "We aim for reliable operation but do not guarantee uninterrupted availability.",
			  "Maintenance or incidents may cause temporary downtime.",
			],
		  },
		  {
			h: "Intellectual property",
			bullets: [
			  "EstiaSync and its branding are protected. You receive a limited right to use the service.",
			  "You keep ownership of your data; you grant us the rights needed to operate the service for you.",
			],
		  },
		  {
			h: "Limitation of liability",
			bullets: [
			  "To the maximum extent permitted by law, we are not liable for indirect or consequential damages.",
			  "You are responsible for your operational decisions and the way you use the information provided.",
			],
		  },
		  {
			h: "Termination",
			bullets: [
			  "We may suspend or terminate access if these Terms are violated.",
			  "You may stop using the service at any time; account deletion can be requested.",
			],
		  },
		  {
			h: "Governing law",
			bullets: [
			  "These Terms are governed by the laws of Greece, unless mandatory rules provide otherwise.",
			],
		  },
		  {
			h: "Contact",
			bullets: [
			  "Email: info@estiasync.com",
			  "Phone: +30 2294 701 845",
			],
		  }
		],
	  },
	},

    modal: { label: "estiasync – promo", close: "Close" },
    flags: { el: "Ελληνικά", en: "English" },
  },
  el: {
    nav: {
      features: "Χαρακτηριστικά",
      how: "Πώς λειτουργεί",
      pricing: "Τιμές",
      faq: "Συχνές ερωτήσεις",
	  contact: "Επικοινωνία",
      signin: "Είσοδος",
    },
    badge:  "Για να δουλεύει η επιχείρησή σου ρολόι.",
	badge: "Για να δουλεύουν όλα ρολόι.",
	hero: {
	  h1a: "Εξυπηρέτηση με",
	  h1b: "απόλυτο συγχρονισμό",
	  p: "Το EstiaSync είναι το απόλυτο εργαλείο για την εστίαση. Δεν είναι άλλο ένα σύστημα παραγγελιοληψίας. Το EstiaSync δεν είναι απλά μια υπηρεσία κρατήσεων αλλά πολλά περισσότερα...",
	  explore: "Δες τα χαρακτηριστικά",
	  watch: "Δες το σύντομο βίντεο",
	  p1dt: "Push Notifications",
	  p1dd: "Δυνατότητα αυτοματοποιημένων άμεσων γνωστοποιήσεων",
	  p2dt: "Εύχρηστες οθόνες",
	  p2dd: "Η κατάλληλη πληροφορία σε κάθε πόστο.",
	},
    social: { trusted: "Μας εμπιστεύονται οι ταχύτερες ομάδες" },
	features: {
	  h2: "Όλα συγκεντρωμένα σε μία υπηρεσία",
	  p: "Για να δουλεύουν όλα ρολόι.",
	  items: [
		{ t: "Διαχείριση πελατολογίου", d: "Εύκολη αναζήτηση/προσθήκη, αναγνώριση κλήσης, πλήρες ιστορικό, αξιολόγηση πελάτη και σημειώσεις." },
		{ t: "Διαχείριση χώρου & τραπεζιών", d: "Αποτύπωση κάτοψης, διαχωρισμός περιοχών/πόστων, έξυπνα φίλτρα, χωρητικότητα ανά τραπέζι και συγχώνευση." },
		{ t: "Εισαγωγή κράτησης", d: "Αναζήτηση με τηλέφωνο/όνομα ή αναγνώριση κλήσης με άμεσο ιστορικό· αποτύπωση διαθεσιμότητας σε γράφημα & ατζέντα." },
		{ t: "Διαχείριση κρατήσεων", d: "Λίστα/κάρτες με άμεση κλήση, επεξεργασία στοιχείων κράτησης/πελάτη, φίλτρα, καταστάσεις και δρομολόγηση αφίξεων." },
		{ t: "Περιβάλλον ψησταριάς", d: "Ομαδοποιημένες παραγγελίες ανά κράτηση, χρόνοι ψησίματος, προγραμματισμός, χρονομετρητές, ειδοποιήσεις και εκτύπωση αυτοκόλλητων." },
		{ t: "Περιβάλλον κουζίνας", d: "Ενέργειες ανά κράτηση (καλωσόρισμα, ορεκτικά, συνοδευτικά, επιδόρπιο) με χρονομετρητές και ειδοποιήσεις." },
		{ t: "Περιβάλλον προσωπικού εξυπηρέτησης", d: "Κάτοψη με χρώματα κατάστασης, οπτική ειδοποίηση εκκρεμοτήτων, καρτέλα τραπεζιού και λίστα ενεργειών ανά πόστο → τραπέζι." },
		{ t: "Εβδομαδιαίο πλάνο βαρδιών/πόστων", d: "Ορισμός βαρδιών ανά πόστο/περιοχή, αυτόματη αναπαραγωγή εβδομάδας και ζωντανή πρόσβαση του προσωπικού." },
		{ t: "Εξατομικευμένες ειδοποιήσεις & smartwatches", d: "Στοχευμένες ειδοποιήσεις στο σέρβις (ανά περιοχή) πέρα από τις ειδοποιήσεις της οθόνης." },
		{ t: "Στατιστικά & αναφορές", d: "Επισκέψεις, κατανάλωση ειδών, μέσοι χρόνοι εκτέλεσης ενεργειών και heatmaps προτιμήσεων." },
		{ t: "Παραμετροποίηση υπηρεσίας", d: "Είδη, κατηγορίες ψησίματος, χρόνοι προετοιμασίας και ρυθμίσεις που προσαρμόζουν τη ροή σου." },
		{ t: "Διαχείριση χρηστών & ρόλων", d: "Λογαριασμοί χρηστών, ρόλοι και δικαιώματα ανά περιβάλλον/πόστο." },
	  ],
	},
	how: {
	  h2: "Ψήθηκες;",
	  p: "Για να παραμείνεις ζεστός, δες τα βήματα που θα ακολουθήσουν.",
	  plans: [
		{
		  name: "Starter",
		  steps: [
			"Επικοινώνησε μαζί μας στο info@estiasync.com ή κάλεσέ μας στο +30 2294 701845.",
			"Ένας εξειδικευμένος συνεργάτης θα σε κατευθύνει σύμφωνα με τις ανάγκες της επιχείρησής σου.",
			"Ο λογαριασμός σου είναι έτοιμος! Ήρθε η ώρα να συναντηθούμε — κλείνουμε ραντεβού στον χώρο σου.",
			"Σου παρουσιάζουμε αναλυτικά τις δυνατότητες και τις προσαρμόζουμε στα μέτρα σου.",
			"Αν χρειαστεί εξοπλισμός, είμαστε εδώ με προτάσεις στο πλαίσιο μιας ολοκληρωμένης λύσης.",
			"Ώρα για μάθημα! Η estiasync αναλαμβάνει την εκπαίδευση του προσωπικού σου.",
			"Τέλεια! Πλέον έχεις κρατήσεις και πελατολόγιο όπου κι αν βρίσκεσαι, όποτε το χρειαστείς.",
		  ],
		},
		{
		  name: "Basic",
		  steps: [
			"Τώρα είσαι έτοιμος για τα επόμενα βήματα — πάμε να τα δούμε παρέα.",
			"Εξειδικευμένος συνεργάτης μας εγκαθιστά τον απαραίτητο εξοπλισμό σε κουζίνα/BBQ και σέρβις, ώστε όλοι να μένετε συγχρονισμένοι.",
			"Η estiasync εκπαιδεύει το προσωπικό ανά πόστο, σύμφωνα με το περιβάλλον εργασίας.",
			"Η ομάδα «πετάει» — κρατήσεις, κουζίνα/BBQ και σέρβις σε απόλυτο συγχρονισμό.",
		  ],
		},
		{
		  name: "Pro",
		  steps: [
			"Νόμιζες ότι αυτό ήταν όλο; Η estiasync κάνει την επιχείρησή σου να δουλεύει ρολόι.",
			"Και μιλώντας για ρολόγια… το εννοούμε: κάθε ενέργεια του σέρβις είναι ζωντανά στο χέρι τους.",
			"Με εβδομαδιαίο πρόγραμμα ανά πόστο, στοχευμένες ειδοποιήσεις καθοδηγούν το προσωπικό σε πραγματικό χρόνο.",
			"Τικ Τοκ.. Το ακούς και εσύ? Είναι η estiasync που έχει κάνει την επιχείρησή σου να δουλέυει ρολόι :)",
		  ],
		},
	  ],
	},	
    screens: {
      h2: "Οθόνες φτιαγμένες για πραγματικές βάρδιες",
      p: "Εύκολη χρήση οθονών αφής. Ξεκάθαρη κατάσταση. Γρήγορη εναλλαγή μεταξύ σταθμών.",
      shots: [
        { name: "Κρατήσεις", hint: "Λίστα + γρήγορη προσθήκη" },
        { name: "BBQ", hint: "Πίνακας προετοιμασίας" },
        { name: "Σέρβις", hint: "Λειτουργία προσωπικού εξυπηρέτησης" },
        { name: "Κουζίνα", hint: "Προβολή ticket" },
      ],
    },
    pricing: {
      h2: "Ξεκάθαρη χρέωση",
      monthly: "Μηνιαία",
      yearly: "Ετήσια",
      yearlyNote: "με 2 μήνες δώρο!",
      plans: {
        starter: {
          name: "Starter",
          features: ["Για ένα κατάστημα", "Χώροι & Τραπέζια", "Διαχείριση πελατών", "Διαχείριση κρατήσεων"],
          cta: "Δοκίμασε την επίδειξη",
        },
        basic: {
          name: "Basic",
          features: ["Ό,τι στο Starter", "Πίνακας BBQ", "Προβολή κουζίνας", "Λειτουργία προσωπικού εξυπηρέτησης"],
          cta: "Δοκίμασε την επίδειξη",
        },
        pro: {
          name: "Pro",
          badge: "Δημοφιλές",
          features: ["Ό,τι στο Basic", "Στατιστικά & Αναφορές", "Προγραμματισμός προσωπικού", "Αυτόματες push ειδοποιήσεις"],
          cta: "Δοκίμασε την επίδειξη",
        },
        multi: {
          name: "Για αλυσίδα καταστημάτων",
          price: "Μίλησέ μας",
          features: ["Ό,τι στο Pro", "Πολλαπλές τοποθεσίες", "Προτεραιότητα υποστήριξης", "Ειδική εκπαίδευση"],
          cta: "Επικοινωνία",
        },
      },
    },
    faq: {
      h2: "Συχνές ερωτήσεις",
      p: (emailNode) => <>Κάτι άλλο; Στείλε email στο {emailNode}.</>,
      items: [
        { q: "Λειτουργεί σε iPhone/iPad;", a: "Ναι. Είναι PWA — εγκαθίσταται στην Αρχική οθόνη και τρέχει πλήρους οθόνης." },
        { q: "Χρειάζομαι ειδικό hardware;", a: "Όχι. Λειτουργεί σε σύγχρονο browser — τηλέφωνα, tablets, laptops, βοηθητικές οθόνες." },
        { q: "Βλέπουν BBQ και κουζίνα διαφορετικές προβολές;", a: "Ναι, κάθε σταθμός έχει τον δικό του ζωντανό πίνακα από τα ίδια δεδομένα." },
        { q: "Πόσο διαρκεί το onboarding;", a: "Οι περισσότερες ομάδες τρέχουν δοκιμαστική βάρδια την ίδια μέρα. Προσκάλεσε το προσωπικό και φύγαμε." },
        { q: "Τι γίνεται με τις ειδοποιήσεις;", a: "Το προσωπικό μπορεί να ενεργοποιήσει push ειδοποιήσεις για κρίσιμα γεγονότα." },
      ],
    },
	final: {
	  h3: "Κάνε αίτηση για αυτοματοποιημένο demo",
	  p: "Συμπλήρωσε τα στοιχεία σου και, μετά την έγκριση, θα λάβεις email με το URL και τα στοιχεία πρόσβασης.",
	  demo: "Κάνε αίτηση για demo",
	  call: "Επικοινωνία",
	},
	footer: {
	  privacy: "Απόρρητο",
	  terms: "Όροι",
	  copyright: () => `© ${new Date().getFullYear()} estiasync.com`,
	},
	legal: {
	  privacy: {
		title: "Πολιτική Απορρήτου",
		updated: "Τελευταία ενημέρωση: 24 Δεκεμβρίου 2025",
		intro:
		  "Η παρούσα Πολιτική Απορρήτου εξηγεί πώς η EstiaSync («εμείς») συλλέγει και χρησιμοποιεί πληροφορίες όταν επισκέπτεσαι τον ιστότοπό μας ή χρησιμοποιείς τις υπηρεσίες μας.",
		sections: [
		  {
			h: "Ποια δεδομένα συλλέγουμε",
			bullets: [
			  "Στοιχεία επικοινωνίας που μας δίνεις (π.χ. όνομα, email, τηλέφωνο).",
			  "Δεδομένα λογαριασμού και λειτουργίας που καταχωρείς στην υπηρεσία (π.χ. κρατήσεις, σημειώσεις πελατών, ρυθμίσεις χώρου/τραπεζιών).",
			  "Τεχνικά δεδομένα (π.χ. συσκευή/φυλλομετρητής, κατά προσέγγιση τοποθεσία, IP, logs) για ασφάλεια και επίλυση προβλημάτων.",
			  "Cookies ή παρόμοιες τεχνολογίες για βασική λειτουργικότητα και βελτίωση απόδοσης.",
			],
		  },
		  {
			h: "Πώς χρησιμοποιούμε τα δεδομένα σου",
			bullets: [
			  "Για την παροχή και λειτουργία της υπηρεσίας (λογαριασμοί, λειτουργίες, υποστήριξη).",
			  "Για επικοινωνία μαζί σου (αιτήματα, onboarding, υποστήριξη).",
			  "Για βελτίωση αξιοπιστίας, απόδοσης και εμπειρίας χρήσης.",
			  "Για ασφάλεια, αποτροπή κακόβουλης χρήσης και συμμόρφωση με υποχρεώσεις του νόμου.",
			],
		  },
		  {
			h: "Νομική βάση επεξεργασίας (GDPR)",
			bullets: [
			  "Εκτέλεση σύμβασης (για την παροχή της υπηρεσίας).",
			  "Έννομο συμφέρον (ασφάλεια, βελτίωση υπηρεσίας).",
			  "Συγκατάθεση (όπου απαιτείται, π.χ. ορισμένα cookies/προωθητικές ενέργειες).",
			  "Νομική υποχρέωση (όπου εφαρμόζεται).",
			],
		  },
		  {
			h: "Κοινοποίηση σε τρίτους",
			bullets: [
			  "Μπορούμε να χρησιμοποιούμε αξιόπιστους παρόχους (π.χ. φιλοξενία, αποστολή email) για τη λειτουργία της υπηρεσίας.",
			  "Δεν πωλούμε τα προσωπικά σου δεδομένα.",
			  "Ενδέχεται να γνωστοποιήσουμε δεδομένα αν απαιτηθεί από τον νόμο ή για την προστασία δικαιωμάτων και ασφάλειας.",
			],
		  },
		  {
			h: "Χρόνος διατήρησης",
			bullets: [
			  "Διατηρούμε δεδομένα μόνο για όσο χρειάζεται για τους σκοπούς που περιγράφονται εδώ, εκτός αν απαιτείται μεγαλύτερη περίοδος από τον νόμο.",
			  "Μπορείς να ζητήσεις διαγραφή δεδομένων λογαριασμού, με την επιφύλαξη τυχόν νομικών/λειτουργικών περιορισμών.",
			],
		  },
		  {
			h: "Ασφάλεια",
			bullets: [
			  "Λαμβάνουμε τεχνικά και οργανωτικά μέτρα για την προστασία των δεδομένων.",
			  "Κανένα σύστημα δεν είναι 100% ασφαλές — χρησιμοποίησε ισχυρούς κωδικούς και προστάτευσε τις συσκευές σου.",
			],
		  },
		  {
			h: "Τα δικαιώματά σου",
			bullets: [
			  "Πρόσβαση, διόρθωση, διαγραφή, περιορισμός, φορητότητα και εναντίωση (όπου εφαρμόζεται).",
			  "Μπορείς να ανακαλέσεις τη συγκατάθεσή σου οποτεδήποτε (όταν η επεξεργασία βασίζεται σε συγκατάθεση).",
			],
		  },
		  {
			h: "Επικοινωνία",
			bullets: [
			  "Email: info@estiasync.com",
			  "Τηλέφωνο: +30 2294 701 845",
			],
		  }
		],
	  },

	  terms: {
		title: "Όροι Χρήσης",
		updated: "Τελευταία ενημέρωση: 24 Δεκεμβρίου 2025",
		intro:
		  "Οι παρόντες Όροι διέπουν την πρόσβαση και χρήση του ιστότοπου και των υπηρεσιών EstiaSync. Χρησιμοποιώντας την υπηρεσία, αποδέχεσαι τους Όρους.",
		sections: [
		  {
			h: "Αντικείμενο υπηρεσίας",
			bullets: [
			  "Η EstiaSync παρέχει εργαλεία λειτουργίας εστιατορίου (π.χ. κρατήσεις, κάτοψη, περιβάλλοντα πόστων, ειδοποιήσεις).",
			  "Ορισμένες λειτουργίες εξαρτώνται από συσκευή/φυλλομετρητή (π.χ. PWA/push notifications).",
			],
		  },
		  {
			h: "Λογαριασμοί & ευθύνη χρήστη",
			bullets: [
			  "Είσαι υπεύθυνος/η για τη διαφύλαξη των στοιχείων σύνδεσης.",
			  "Οφείλεις να καταχωρείς νόμιμα δεδομένα και να έχεις δικαίωμα χρήσης τους.",
			],
		  },
		  {
			h: "Αποδεκτή χρήση",
			bullets: [
			  "Απαγορεύεται η κακόβουλη χρήση (π.χ. hacking, scraping, παρεμπόδιση λειτουργίας).",
			  "Απαγορεύεται η ανάρτηση παράνομου περιεχομένου ή περιεχομένου που παραβιάζει δικαιώματα τρίτων.",
			],
		  },
		  {
			h: "Demo/δοκιμή, τιμολόγηση & πληρωμές",
			bullets: [
			  "Η πρόσβαση σε demo/δοκιμή μπορεί να έχει περιορισμούς και να διακοπεί.",
			  "Τα επί πληρωμή πακέτα, οι όροι χρέωσης και τυχόν τιμολόγηση συμφωνούνται ξεχωριστά (όπου εφαρμόζεται).",
			],
		  },
		  {
			h: "Διαθεσιμότητα",
			bullets: [
			  "Στόχος μας είναι η αξιόπιστη λειτουργία, χωρίς να εγγυόμαστε αδιάλειπτη διαθεσιμότητα.",
			  "Συντήρηση ή συμβάντα μπορεί να προκαλέσουν προσωρινή διακοπή.",
			],
		  },
		  {
			h: "Πνευματική ιδιοκτησία",
			bullets: [
			  "Η EstiaSync και τα διακριτικά γνωρίσματα προστατεύονται. Παρέχεται περιορισμένο δικαίωμα χρήσης της υπηρεσίας.",
			  "Τα δεδομένα σου παραμένουν δικά σου· μας παραχωρείς τα δικαιώματα που απαιτούνται για να λειτουργούμε την υπηρεσία για λογαριασμό σου.",
			],
		  },
		  {
			h: "Περιορισμός ευθύνης",
			bullets: [
			  "Στο μέγιστο επιτρεπτό από τον νόμο, δεν ευθυνόμαστε για έμμεσες ή αποθετικές ζημίες.",
			  "Είσαι υπεύθυνος/η για τις επιχειρησιακές αποφάσεις και τον τρόπο χρήσης των πληροφοριών που παρέχονται.",
			],
		  },
		  {
			h: "Διακοπή / τερματισμός",
			bullets: [
			  "Μπορούμε να αναστείλουμε ή να τερματίσουμε πρόσβαση σε περίπτωση παραβίασης των Όρων.",
			  "Μπορείς να σταματήσεις να χρησιμοποιείς την υπηρεσία οποτεδήποτε· μπορείς να ζητήσεις διαγραφή λογαριασμού.",
			],
		  },
		  {
			h: "Εφαρμοστέο δίκαιο",
			bullets: [
			  "Οι Όροι διέπονται από το δίκαιο της Ελλάδας, εκτός αν ισχύουν αναγκαστικοί κανόνες διαφορετικά.",
			],
		  },
		  {
			h: "Επικοινωνία",
			bullets: [
			  "Email: info@estiasync.com",
			  "Τηλέφωνο: +30 2294 701 845",
			],
		  }
		],
	  },
	},

    modal: { label: "estiasync – promo", close: "Κλείσιμο" },
    flags: { el: "Ελληνικά", en: "English" },
  },
};

const CONTACT_EMAIL = "info@estiasync.com";
const CONTACT_PHONE_DISPLAY = "2294 701 845";
const CONTACT_PHONE_TEL = "+302294701845";



function useI18n() {
  const [lang, setLang] = useState("en");

  useEffect(() => {
    // Restore manual choice
    const saved = localStorage.getItem("estia_lang");
    if (saved === "el" || saved === "en") {
      setLang(saved);
      return;
    }

    // Auto-detect Greece by IP; fall back to language/timezone
    (async () => {
      try {
        const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        const data = await res.json().catch(() => ({}));
        if (data?.country_code === "GR" || data?.country === "GR") {
          setLang("el");
        } else {
          // Fallback heuristics
          const nav = navigator.language || (navigator.languages && navigator.languages[0]) || "en";
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
          if (nav.toLowerCase().startsWith("el") || tz === "Europe/Athens") setLang("el");
        }
      } catch (e) {
        const nav = navigator.language || (navigator.languages && navigator.languages[0]) || "en";
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (nav.toLowerCase().startsWith("el") || tz === "Europe/Athens") setLang("el");
      }
    })();
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (path, ...args) => {
    const parts = path.split(".");
    let cur = tr[lang];
    for (const p of parts) cur = cur?.[p];
    return typeof cur === "function" ? cur(...args) : cur ?? path;
  };

  const setLanguage = (lng) => {
    setLang(lng);
    try { localStorage.setItem("estia_lang", lng); } catch {}
  };

  const videoSrc = lang === "el" ? "/promo_gr.mp4" : "/promo_en.mp4";

  return { lang, setLanguage, t, videoSrc };
}

// -----------------------------
// Utility
// -----------------------------
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(!!m.matches);
    onChange();
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
};

const fadeIn = (delay = 0, distance = 16) => ({
  hidden: { opacity: 0, y: distance },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }
  }
});

function Container({ children, className = "", ...props }) {
  return (
    <div {...props} className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
      {children}
    </div>
  );
}

function ScrollToTopButton({ prefersReduced }) {
  const [show, setShow] = useState(false);

  const getScrollEl = () =>
    document.scrollingElement || document.documentElement || document.body;

  useEffect(() => {
    let raf = 0;

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const el = getScrollEl();
        setShow((el?.scrollTop || window.scrollY || 0) > 300);
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const goTop = () => {
    const el = getScrollEl();

    // main path (best)
    el?.scrollTo?.({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });

    // iOS Safari safety fallbacks
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
  };

  return (
    <button
      type="button"
      onClick={goTop}
      aria-label="Back to top"
      className={[
        "fixed bottom-6 right-6 z-50",
        "rounded-2xl border border-slate-200 bg-white/90 backdrop-blur",
        "shadow-lg shadow-slate-200/60",
        "p-3 hover:bg-white transition-all",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2",
        show ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none",
      ].join(" ")}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 5l-6 6m6-6l6 6M12 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </button>
  );
}


// -----------------------------
// Hero (animated gradient, video)
// -----------------------------
function Hero({ onOpenVideo, t, videoSrc }) {
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 0.4], [1, 0.96]);
  const y = useTransform(scrollYProgress, [0, 0.4], [0, -10]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-white to-white">
      {/* Vibes: blurred orbs */}
      <div className="pointer-events-none absolute -top-24 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-sky-200 blur-3xl opacity-40" />
      <div className="pointer-events-none absolute -bottom-24 -right-16 h-96 w-96 rounded-full bg-emerald-200 blur-3xl opacity-40" />

      <motion.div style={{ scale, y }}>
        <Container className="relative py-20 sm:py-28">
			<div className="grid lg:grid-cols-2 gap-12 items-center">
				<motion.div
				  initial={{ opacity: 0, y: 10, scale: 0.99 }}
				  whileInView={{ opacity: 1, y: 0, scale: 1 }}
				  viewport={{ once: true, amount: 0.6 }}
				  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
				  className="lg:col-span-2 flex justify-center"
				>
				  <div className="text-center">
					<div className="relative inline-block">
					  {/* soft glow */}
					  <div className="pointer-events-none absolute -inset-10 rounded-[40px] bg-gradient-to-r from-sky-200/45 via-cyan-200/35 to-emerald-200/45 blur-2xl" />
					  <div className="relative leading-none">
  <BrandMark
    href={null}
    size={62}
    variant="hero"
    withIcon={false}
    textPx={"clamp(64px, 12vw, 120px)"}
    tmPx={"clamp(10px, 2.2vw, 16px)"}
    className="font-extrabold tracking-tight"
  />
</div>
</div>

					<div className="mt-3 text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
					  {t("badge")}
					</div>
				  </div>
				</motion.div>
			  <motion.div
				initial="hidden"
				whileInView="show"
				viewport={{ once: true, amount: 0.4 }}
				variants={fadeIn(0)}
			  >
				<h1 className="mt-5 text-4xl sm:text-6xl font-extrabold tracking-tight">
				  <span className="block text-slate-900">{t("hero.h1a")}</span>
				  <span className="bg-gradient-to-r from-sky-500 via-cyan-500 to-emerald-500 bg-clip-text text-transparent">{t("hero.h1b")}</span>
				</h1>
				<p className="mt-5 text-lg text-slate-600 max-w-xl">
				  {t("hero.p")}
				</p>
				<div className="mt-7 flex flex-col sm:flex-row gap-3">
				  <a
					href="#features"
					className="relative inline-flex items-center justify-center rounded-2xl px-5 py-3 font-semibold text-white shadow-lg shadow-sky-200 transition-transform active:scale-[0.98] bg-gradient-to-r from-slate-900 to-slate-800 hover:to-black"
				  >
					{t("hero.explore")}
					<span className="ml-1">→</span>
				  </a>

				  <button
					onClick={onOpenVideo}
					className="group inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white/70 px-5 py-3 font-semibold hover:bg-white/90"
				  >
					<span className="mr-2">▶</span> {t("hero.watch")}
					<span className="ml-2 translate-x-0 transition-transform group-hover:translate-x-1">⟶</span>
				  </button>
				</div>
				<dl className="mt-8 grid grid-cols-2 gap-6 text-sm">
				  <div>
					<dt className="text-slate-500">{t("hero.p1dt")}</dt>
					<dd className="font-semibold text-slate-900">{t("hero.p1dd")}</dd>
				  </div>
				  <div>
					<dt className="text-slate-500">{t("hero.p2dt")}</dt>
					<dd className="font-semibold text-slate-900">{t("hero.p2dd")}</dd>
				  </div>
				</dl>
			  </motion.div>

			  <motion.div
				initial={{ opacity: 0, scale: 0.98 }}
				whileInView={{ opacity: 1, scale: 1 }}
				viewport={{ once: true, amount: 0.4 }}
				transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
				className="relative lg:pl-8"
			  >
				<button
				  type="button"
				  onClick={onOpenVideo}
				  className="group relative aspect-[16/10] w-full overflow-hidden rounded-3xl bg-slate-900 shadow-2xl ring-1 ring-slate-200 text-left"
				  aria-label={t("hero.watch")}
				  style={{cursor: 'pointer'}}
				>
				  <video
					src={videoSrc}
					muted
					playsInline
					autoPlay
					loop
					preload="metadata"
					className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
				  />
				  {/* subtle overlay so text stays readable */}
				  <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-black/35 via-transparent to-black/20" />

				  {/* play hint */}
				  <div className="pointer-events-none absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-xl bg-white/90 px-3 py-2 text-sm font-semibold text-slate-900 shadow">
					<span>▶</span>
				  </div>
				</button>
			  </motion.div>
			</div>
        </Container>
      </motion.div>
    </section>
  );
}

// -----------------------------
// Social Proof
// -----------------------------
function SocialProof({ t }) {
  return (
    <section>
      <Container className="scroll-mt-24 py-8 sm:py-10">
        <motion.p
          className="text-center text-xs sm:text-sm uppercase tracking-wider text-slate-500"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.8 }}
          variants={fadeIn(0)}
        >
          {t("social.trusted")}
        </motion.p>
		<div className="mt-4 flex flex-wrap justify-center gap-2 sm:gap-3">
		  <motion.a
			href="https://www.argentina-restaurant.gr/"
			target="_blank"
			rel="noopener noreferrer"
			aria-label="Visit Argentina Restaurant website"
			className="group inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white/80 ring-1 ring-slate-200 hover:shadow-md hover:border-slate-300
					   w-[220px] h-[110px] p-1
					   sm:w-[260px] sm:h-[130px] sm:p-1.5
					   lg:w-[280px] lg:h-[140px] lg:p-2"
			initial={{ opacity: 0, y: 8 }}
			whileInView={{ opacity: 1, y: 0 }}
			whileHover={{ scale: 1.02 }}
			whileTap={{ scale: 0.99 }}
			viewport={{ once: true }}
			title="Argentina Restaurant - opens in new tab"
		  >
			<img
			  src="/companies_logos/argentina.png"
			  alt="Argentina"
			  className="h-[100%] w-[100%] object-contain"
			/>
		  </motion.a>

		  <motion.a
			href="https://www.facebook.com/p/%CE%A8%CE%B1%CF%81%CE%BF%CF%84%CE%B1%CE%B2%CE%AD%CF%81%CE%BD%CE%B1-%CE%A6%CE%AC%CF%81%CE%BF%CF%82-100057364342382/?locale=el_GR"
			target="_blank"
			rel="noopener noreferrer"
			aria-label="Visit Faros Restaurant website"
			className="group inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white/80 ring-1 ring-slate-200 hover:shadow-md hover:border-slate-300
					   w-[220px] h-[110px] p-1
					   sm:w-[260px] sm:h-[130px] sm:p-1.5
					   lg:w-[280px] lg:h-[140px] lg:p-2"
			initial={{ opacity: 0, y: 8 }}
			whileInView={{ opacity: 1, y: 0 }}
			whileHover={{ scale: 1.02 }}
			whileTap={{ scale: 0.99 }}
			viewport={{ once: true }}
			title="Faros Restaurant - opens in new tab"
		  >
			<img
			  src="/companies_logos/faros.png"
			  alt="Faros"
			  className="h-[100%] w-[100%] object-contain"
			/>
		  </motion.a>
		</div>
      </Container>
    </section>
  );
}

// -----------------------------
// Features (gradient cards)
// -----------------------------
function AutoFocusFeatureCard({ f, i, prefersReduced }) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Only consider the middle "band" of the viewport as focused
    const obs = new IntersectionObserver(
      ([entry]) => setFocused(entry.isIntersecting),
      {
        root: null,
        rootMargin: "-40% 0px -40% 0px", // middle ~20% band
        threshold: 0.01,
      }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: i * 0.04, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl p-[1px] bg-gradient-to-b from-slate-100 to-white shadow-sm hover:shadow ring-1 ring-slate-200"
    >
      <div className="rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="text-lg font-semibold text-slate-900">{f.t}</div>
          <span className={`text-slate-400 transition-transform ${focused ? "rotate-180" : ""}`}>▾</span>
        </div>

        <AnimatePresence initial={false}>
          {focused && (
            <motion.div
              initial={prefersReduced ? false : { height: 0, opacity: 0 }}
              animate={prefersReduced ? false : { height: "auto", opacity: 1 }}
              exit={prefersReduced ? false : { height: 0, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="overflow-hidden"
            >
              <div className="mt-2 text-slate-600 text-sm leading-relaxed">
                {f.d}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}



function Features({ t, prefersReduced }) {
  const items = t("features.items");
  return (
    <section id="features" className="scroll-mt-24 bg-slate-50">
      <Container className="py-[30px]">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t("features.h2")}</h2>
          <p className="mt-3 text-slate-600">{t("features.p")}</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
			{items.map((f, i) => (
			  <AutoFocusFeatureCard key={`${f.t}-${i}`} f={f} i={i} prefersReduced={prefersReduced} />
			))}
        </div>
      </Container>
    </section>
  );
}

// -----------------------------
// How it works (plans)
// -----------------------------
const PLAN_THEMES = [
  { line: "bg-sky-200", dot: "bg-sky-500", stepDot: "bg-sky-300", stepBorder: "border-sky-200" },     // Starter
  { line: "bg-amber-200", dot: "bg-amber-500", stepDot: "bg-amber-300", stepBorder: "border-amber-200" }, // Basic
  { line: "bg-emerald-200", dot: "bg-emerald-500", stepDot: "bg-emerald-300", stepBorder: "border-emerald-200" }, // Pro
];

function HowItWorks({ t, lang }) {
  const plans = t("how.plans") || [];

  // default: only Starter open
  const [openMap, setOpenMap] = useState(() => ({}));

  useEffect(() => {
    setOpenMap((prev) => {
      const next = {};
      plans.forEach((_, idx) => {
        next[idx] = prev[idx] ?? false;
      });
      return next;
    });
  }, [plans.length]);

  const toggle = (idx) => setOpenMap((p) => ({ ...p, [idx]: !p[idx] }));

  const listV = {
    open: { height: "auto", opacity: 1, transition: { duration: 0.25 } },
    collapsed: { height: 0, opacity: 0, transition: { duration: 0.2 } },
  };

  const itemV = {
    open: { opacity: 1, y: 0 },
    collapsed: { opacity: 0, y: -6 },
  };

  const lblExpand = lang === "el" ? "Άνοιγμα" : "Expand";
  const lblCollapse = lang === "el" ? "Κλείσιμο" : "Collapse";

  return (
    <section id="how" className="scroll-mt-24">
      <Container className="py-[30px]">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {t("how.h2")}
          </h2>
          <p className="mt-3 text-slate-600">{t("how.p")}</p>
        </div>

        {/* Continuous timeline, but each plan has its own colored line segment */}
        <div className="mt-10">
          <div className="space-y-0">
            {plans.map((pl, planIdx) => {
              const isOpen = !!openMap[planIdx];
              const contentId = `plan-steps-${planIdx}`;
              const theme = PLAN_THEMES[planIdx % PLAN_THEMES.length];

              return (
                <div key={`${pl.name}-${planIdx}`} className="relative pt-3 pb-6">
                  {/* colored line for this plan (fills this block’s height) */}
                  <div className={`absolute left-4 top-0 bottom-0 w-px ${theme.line}`} />

                  {/* Plan header */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35 }}
                    className="relative pl-12"
                  >
                    <div className={`absolute left-4 top-6 -translate-x-1/2 h-3.5 w-3.5 rounded-full ${theme.dot} ring-4 ring-white`} />

                    <button
                      type="button"
                      onClick={() => toggle(planIdx)}
                      aria-expanded={isOpen}
                      aria-controls={contentId}
                      className="w-full rounded-3xl bg-white/90 px-5 py-4 text-left shadow-sm ring-1 ring-slate-200 hover:bg-white transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-xl font-extrabold text-slate-900">{pl.name}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {(pl.steps || []).length} • {isOpen ? lblCollapse : lblExpand}
                          </div>
                        </div>

                        <span className={`transition-transform text-slate-500 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true">
                          ▾
                        </span>
                      </div>
                    </button>
                  </motion.div>

                  {/* Steps (indented more than plan title + bigger font/weight) */}
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={listV}
                        className="overflow-hidden"
                        id={contentId}
                      >
                        <motion.ul
                          variants={{ open: { transition: { staggerChildren: 0.05 } } }}
                          className="mt-4 space-y-3"
                        >
							{(pl.steps || []).map((s, i) => {
							  const isContactStep = planIdx === 0 && i === 0; // Starter, first step

							  return (
								<motion.li
								  key={i}
								  variants={itemV}
								  initial="collapsed"
								  animate="open"
								  exit="collapsed"
								  className="relative pl-16"
								>
								  <div className={`absolute left-4 top-5 -translate-x-1/2 h-2.5 w-2.5 rounded-full ${theme.stepDot} ring-4 ring-white`} />

								  <div
									className={[
									  "rounded-3xl bg-white px-6 py-5 ring-1 ring-slate-200 shadow-sm",
									  "border-l-4", theme.stepBorder,
									  "text-base sm:text-lg font-semibold text-slate-700 leading-snug",
									].join(" ")}
								  >
									{isContactStep ? (
									  <div className="space-y-3">
										<div>
										  {lang === "el"
											? "Επικοινώνησε μαζί μας:"
											: "Contact us:"}
										</div>

										<div className="flex flex-col sm:flex-row gap-2">
										  <a
											href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("estiasync – ενδιαφέρον/interest")}`}
											className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 font-semibold text-slate-900 hover:bg-white"
										  >
											✉️ {CONTACT_EMAIL}
										  </a>

										  <a
											href={`tel:${CONTACT_PHONE_TEL}`}
											className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 font-semibold text-slate-900 hover:bg-white"
										  >
											📞 {CONTACT_PHONE_DISPLAY}
										  </a>
										</div>
									  </div>
									) : (
									  s
									)}
								  </div>
								</motion.li>
							  );
							})}
                        </motion.ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}


// -----------------------------
// Pricing (toggle)
// -----------------------------
function Pricing({ t }) {
  const [yearly, setYearly] = useState(true);
  const price = useMemo(
    () => ({
      starter: yearly ? "€550/yr" : "€55/mo",
      basic: yearly ? "€950/yr" : "€95/mo",
      pro: yearly ? "€1450/yr" : "€145/mo",
    }),
    [yearly]
  );

  const plans = t("pricing.plans");

  return (
    <section id="pricing" className="scroll-mt-24 bg-slate-50">
      <Container className="py-[30px]">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t("pricing.h2")}</h2>
        </div>

        {/* Billing toggle */}
        <div className="mt-6 flex items-center gap-3">
          <span className={`text-sm ${!yearly ? "text-slate-900" : "text-slate-500"}`}>{t("pricing.monthly")}</span>
          <button
            onClick={() => setYearly(!yearly)}
            className="relative inline-flex h-7 w-12 items-center rounded-full bg-slate-200 transition-colors"
            aria-label="Toggle billing"
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                yearly ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <span className={`text-sm ${yearly ? "text-slate-900" : "text-slate-500"}`}>{t("pricing.yearly")} • <i>{t("pricing.yearlyNote")}</i></span>
        </div>

        {/* Plans */}
        <div className="mt-10 mx-auto w-full max-w-7xl">
          <div className="flex flex-col md:flex-row items-stretch justify-center gap-6">
            {/* Starter */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex-1 min-w-0 rounded-3xl bg-white p-6 ring-1 ring-slate-200 shadow-sm h-full"
            >
              <div className="text-sm font-semibold text-slate-500">{plans.starter.name}</div>
              <div className="mt-2 text-3xl font-bold">{price.starter}</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                {plans.starter.features.map((f, i) => (<li key={i}>• {f}</li>))}
              </ul>
              <a
                href={`mailto:info@estiasync.com?subject=${encodeURIComponent(`Pricing request – ${plans.starter.name}`)}`}
                className="mt-6 inline-flex rounded-xl bg-slate-900 px-4 py-2 text-white font-semibold hover:bg-black"
                
              >
                {plans.starter.cta}
              </a>
            </motion.div>

            {/* Basic */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="flex-1 min-w-0 rounded-3xl bg-white p-6 ring-1 ring-slate-200 shadow-sm h-full"
            >
              <div className="text-sm font-semibold text-slate-500">{plans.basic.name}</div>
              <div className="mt-2 text-3xl font-bold">{price.basic}</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                {plans.basic.features.map((f, i) => (<li key={i}>• {f}</li>))}
              </ul>
              <a
                href={`mailto:info@estiasync.com?subject=${encodeURIComponent(`Pricing request – ${plans.basic.name}`)}`}
                className="mt-6 inline-flex rounded-xl bg-slate-900 px-4 py-2 text-white font-semibold hover:bg-black"
                
              >
                {plans.basic.cta}
              </a>
            </motion.div>

            {/* Pro */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex-1 min-w-0 relative rounded-3xl p-6 ring-1 ring-slate-800 shadow-2xl bg-gradient-to-b from-slate-900 to-black text-white h-full"
            >
              {plans.pro.badge && (
                <span className="absolute -top-3 right-4 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white shadow">
                  {plans.pro.badge}
                </span>
              )}
              <div className="text-sm font-semibold text-slate-300">{plans.pro.name}</div>
              <div className="mt-2 text-3xl font-bold">{price.pro}</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-200">
                {plans.pro.features.map((f, i) => (<li key={i}>• {f}</li>))}
              </ul>
              <a
                href={`mailto:info@estiasync.com?subject=${encodeURIComponent(`Pricing request – ${plans.pro.name}`)}`}
                className="mt-6 inline-flex rounded-xl bg-white px-4 py-2 font-semibold text-slate-900"
                
              >
                {plans.pro.cta}
              </a>
            </motion.div>

            {/* Multi-site */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="flex-1 min-w-0 rounded-3xl bg-white p-6 ring-1 ring-slate-200 shadow-sm h-full"
            >
              <div className="text-sm font-semibold text-slate-500">{plans.multi.name}</div>
              <div className="mt-2 text-3xl font-bold">{plans.multi.price || ""}</div>
              <ul className="mt-4 space-y-2 text-sm text-slate-600">
                {plans.multi.features.map((f, i) => (<li key={i}>• {f}</li>))}
              </ul>
              <a
                href="mailto:sales@estiasync.com?subject=Multi-site%20plan"
                className="mt-6 inline-flex rounded-xl border border-slate-300 px-4 py-2 font-semibold"
              >
                {plans.multi.cta}
              </a>
            </motion.div>
          </div>
        </div>
      </Container>
    </section>
  );
}

// -----------------------------
// FAQ (collapsible)
// -----------------------------
function FAQ({ t }) {
  const faqs = t("faq.items") || [];

  const emailNode = (
    <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
      {CONTACT_EMAIL}
    </a>
  );

  return (
    <section id="faq" className="scroll-mt-24">
      <Container className="py-[30px]">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {t("faq.h2")}
          </h2>
          <p className="mt-3 text-slate-600">{t("faq.p", emailNode)}</p>
        </div>

        <div className="mt-8 grid gap-3">
          {faqs.map((f, i) => (
            <details
              key={i}
              className="group rounded-2xl bg-white p-5 ring-1 ring-slate-200 shadow-sm open:shadow-md transition-shadow"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                <span className="font-semibold text-slate-900">{f.q}</span>
                <span className="text-slate-500 transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-2 text-sm text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </Container>
    </section>
  );
}


const buildDemoMailto = (lang) => {
  const to = "info@estiasync.com";
  const subject = lang === "el"
    ? "Αίτηση για αυτοματοποιημένο demo (estiasync)"
    : "Request for automated demo (estiasync)";

  const body = lang === "el"
    ? [
        "Καλησπέρα σας,",
        "",
        "Θα ήθελα πρόσβαση σε αυτοματοποιημένο demo του EstiaSync.",
        "",
        "Στοιχεία επιχείρησης:",
        "- Επωνυμία:",
        "- Περιοχή/Πόλη:",
        "- Ονοματεπώνυμο υπευθύνου:",
        "- Τηλέφωνο:",
        "- Email επικοινωνίας:",
        "- Αριθμός τραπεζιών/θέσεων (προαιρετικά):",
        "- Σχόλια/ανάγκες:",
        "",
        "Ευχαριστώ!",
      ].join("\n")
    : [
        "Hello,",
        "",
        "I’d like access to an automated EstiaSync demo.",
        "",
        "Restaurant details:",
        "- Business name:",
        "- City/Area:",
        "- Contact name:",
        "- Phone:",
        "- Contact email:",
        "- Number of tables/seats (optional):",
        "- Notes/needs:",
        "",
        "Thank you!",
      ].join("\n");

  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};


// -----------------------------
// Final CTA + Footer
// -----------------------------
function FinalCTA({ t, lang }) {
  const demoHref = buildDemoMailto(lang);
  return (
    <section className="scroll-mt-24 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 -bottom-24 h-64 bg-gradient-to-t from-slate-100 to-transparent"/>
      <Container className="py-[30px]">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 sm:p-12 ring-1 ring-slate-700"
        >
          <h3 className="text-3xl sm:text-4xl font-bold text-white">{t("final.h3")}</h3>
          <p className="mt-2 text-slate-300">{t("final.p")}</p>
			<div className="mt-6 flex flex-col sm:flex-row gap-3">
			  <a
				href={demoHref}
				className="inline-flex items-center justify-center rounded-2xl bg-white px-5 py-3 font-semibold text-slate-900"
			  >
				{t("final.demo")}
			  </a>

			  <a
				href="mailto:info@estiasync.com?subject=estiasync%20intro%20call"
				className="inline-flex items-center justify-center rounded-2xl border border-slate-500 px-5 py-3 font-semibold text-white"
			  >
				{t("final.call")}
			  </a>
			</div>
        </motion.div>
      </Container>
    </section>
  );
}

function Footer({ t, onOpenLegal }) {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <Container className="py-8 text-sm text-slate-600 flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/bbq_service_icon.ico" alt="" className="h-5 w-5 rounded" />
          <span>{t("footer.copyright")}</span>
        </div>

        <nav className="flex items-center gap-5">
          <a href="/login" className="hover:text-slate-900">{t("nav.signin")}</a>

          <button
            type="button"
            onClick={() => onOpenLegal("privacy")}
            className="hover:text-slate-900"
          >
            {t("footer.privacy")}
          </button>

          <button
            type="button"
            onClick={() => onOpenLegal("terms")}
            className="hover:text-slate-900"
          >
            {t("footer.terms")}
          </button>
        </nav>
      </Container>
    </footer>
  );
}


// -----------------------------
// Video Modal
// -----------------------------
function VideoModal({ open, onClose, t, videoSrc }) {
  const vRef = useRef(null);

  // Start from beginning with sound when opened
  useEffect(() => {
    if (!open) return;

    const v = vRef.current;
    if (!v) return;

    // ensure "play once"
    v.loop = false;

    // enable sound
    v.muted = false;
    v.volume = 1;

    try { v.currentTime = 0; } catch {}

    // try to autoplay (may be blocked by some iOS settings; controls are enabled anyway)
    const p = v.play?.();
    if (p?.catch) p.catch(() => {});
  }, [open, videoSrc]);

  // Close on ESC + stop video on close
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      const v = vRef.current;
      if (v) {
        try { v.pause(); } catch {}
        try { v.currentTime = 0; } catch {}
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      role="dialog"
      aria-modal
      onMouseDown={onClose} // click outside closes
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-4xl overflow-hidden rounded-2xl bg-black shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()} // prevent inside clicks from closing
      >
        <div className="aspect-video w-full">
          <video
            ref={vRef}
            src={videoSrc}
            controls
            autoPlay
            playsInline
            onEnded={onClose} // close when finished
            className="h-full w-full"
          />
        </div>

        <div className="flex items-center justify-between bg-slate-900 px-4 py-3">
          <span className="text-slate-200 text-sm">{t("modal.label")}</span>
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-200"
          >
            {t("modal.close")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}


function LegalModal({ openType, onClose, t }) {
  const open = !!openType;
  const doc = openType ? t(`legal.${openType}`) : null;

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    // lock scroll behind modal
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !doc) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4"
      role="dialog"
      aria-modal
      onMouseDown={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 12 }}
        transition={{ duration: 0.25 }}
        className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between bg-slate-900 px-4 py-3">
          <div className="text-slate-200 text-sm font-semibold">{doc.title}</div>
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-200"
          >
            {t("modal.close")}
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-6">
          <div className="text-xs text-slate-500">{doc.updated}</div>
          <p className="mt-3 text-sm text-slate-700">{doc.intro}</p>

          <div className="mt-6 space-y-6">
            {doc.sections?.map((s, idx) => (
              <section key={idx}>
                <h4 className="text-base font-semibold text-slate-900">{s.h}</h4>

                {s.p ? <p className="mt-2 text-sm text-slate-700">{s.p}</p> : null}

                {Array.isArray(s.bullets) && s.bullets.length > 0 ? (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700">
                    {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}


// -----------------------------
// Page Shell
// -----------------------------
export default function MarketingHome() {
	
  const [videoOpen, setVideoOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(null);
  
  const prefersReduced = usePrefersReducedMotion();

  const { scrollYProgress } = useScroll();
  const width = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  
  const cssReady = useStyleSheet("/marketing.css");

  const { lang, setLanguage, t, videoSrc } = useI18n();

  if (!cssReady) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#fff"
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          border: "3px solid rgba(0,0,0,0.1)",
          borderTopColor: "#0ea5e9",
          animation: "spin 1s linear infinite"
        }}/>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <div id="marketing-root" className="min-h-screen bg-white font-sans antialiased  scroll-smooth">
      {/* Scroll progress */}
      {!prefersReduced && (
        <motion.div style={{ width }} className="fixed left-0 top-0 z-[60] h-0.5 bg-gradient-to-r from-sky-500 via-cyan-500 to-emerald-500" />
      )}

      <MarketingTopNav t={t} lang={lang} setLanguage={setLanguage} />
      <main>
        <Hero t={t} videoSrc={videoSrc} onOpenVideo={() => setVideoOpen(true)} />
        <SocialProof t={t} />
        <Features t={t} prefersReduced={prefersReduced} />
        <HowItWorks t={t} lang={lang} />
        {SHOW_PRODUCT_PROMOS && <OrderingPromoSection lang={lang} />}
        {SHOW_PRODUCT_PROMOS && <BuzzPromoSection lang={lang} />}
        <Pricing t={t} />
        <FAQ t={t} />
        <FinalCTA t={t} lang={lang} />
      </main>
	  <Footer t={t} onOpenLegal={setLegalOpen} />
	  <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} t={t} videoSrc={videoSrc} />
	  <LegalModal openType={legalOpen} onClose={() => setLegalOpen(null)} t={t} />
	  <ScrollToTopButton prefersReduced={prefersReduced} />
    </div>
  );
}
